import React from 'react';
import { alpha, useTheme } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { Box, Button, CircularProgress, Typography } from 'common/components/material';
import { useDeleteEndorsedSkill, useUpdateEndorsedSkill } from 'features/profiles';
import InteractiveEndorsedSkillRating from 'features/org-root/components/InteractiveEndorsedSkillRating';
import { EndorsedSkillWithEndorsers, Skill } from 'features/skills';
import moment from 'moment';

type SkillEndorsementSectionProps = {
  profileId: string;
  profileFirstName?: string;
  skill: Skill;
  endorsement?: EndorsedSkillWithEndorsers;
  isLoading?: boolean;
};

export const SkillEndorsementSection: React.FC<SkillEndorsementSectionProps> = ({
  profileId,
  profileFirstName,
  skill,
  endorsement,
  isLoading,
}) => {
  const { palette } = useTheme();
  const isEndorsed = !!endorsement?.isEndorsedByLoggedInUser;
  const currentRating = endorsement?.loggedInUserRating;
  const endorsementDate = endorsement?.loggedInUserEndorsementDate;
  const { mutate: updateEndorsement, isLoading: isUpdating } = useUpdateEndorsedSkill(
    profileId,
    skill.id,
  );
  const { mutate: removeEndorsement, isLoading: isRemoving } = useDeleteEndorsedSkill(
    profileId,
    skill.id,
  );
  const isSaving = isUpdating || isRemoving;

  const saveRating = (rating: number) => updateEndorsement({ rating });

  const handleRating = (_event: React.MouseEvent, rating: number | null) => {
    if (rating) saveRating(rating);
  };

  const handleEndorsement = () => {
    if (isEndorsed) {
      removeEndorsement(undefined);
    } else {
      saveRating(0);
    }
  };

  const subject = profileFirstName || 'this person';

  return (
    <Box
      sx={{
        border: `1px solid ${palette.Divider}`,
        borderRadius: 2,
        backgroundColor: palette.Card.Bg,
        px: 1.25,
        py: 1,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '96px repeat(4, 104px)' },
          columnGap: 0.75,
          rowGap: 0.75,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body3"
          fontWeight={500}
          sx={{ gridColumn: { xs: '1', sm: '1 / 5' } }}
        >
          Endorse {subject} for {skill.name}
        </Typography>
        <Button
          btntype={isEndorsed ? 'secondary' : 'primary'}
          startIcon={isEndorsed ? <CheckCircleRoundedIcon /> : <AddCircleIcon />}
          size="small"
          disabled={isLoading || isSaving}
          onClick={handleEndorsement}
          sx={{
            gridColumn: { xs: '1', sm: '5' },
            width: { xs: 'fit-content', sm: '100%' },
            minWidth: 92,
            minHeight: 30,
            height: 30,
            px: 1,
            fontSize: '0.7rem',
            '& .MuiButton-startIcon': { mr: 0.5 },
            '& .MuiSvgIcon-root': { fontSize: 15 },
            ...(isEndorsed && {
              color: palette.success.main,
              backgroundColor: alpha(palette.success.main, 0.1),
              border: `1px solid ${alpha(palette.success.main, 0.55)}`,
              '&:hover': {
                color: palette.success.main,
                backgroundColor: alpha(palette.success.main, 0.16),
                borderColor: palette.success.main,
              },
            }),
          }}
        >
          {isSaving ? (
            <CircularProgress size={16} color="inherit" />
          ) : isEndorsed ? (
            'Endorsed'
          ) : (
            'Endorse'
          )}
        </Button>

        <Typography
          variant="caption"
          fontWeight={500}
          color={palette.Text.SubHeadline}
          sx={{ gridColumn: '1' }}
        >
          Proficiency
        </Typography>
        <Box
          sx={{
            gridColumn: { xs: '1', sm: '2 / 6' },
            minWidth: 0,
            overflowX: 'auto',
            '& .MuiToggleButtonGroup-root': {
              gap: 0.75,
              justifyContent: 'flex-start',
            },
            '& .MuiToggleButtonGroup-grouped': {
              border: `1px solid ${palette.Divider} !important`,
              borderRadius: '6px !important',
              ml: '0 !important',
            },
          }}
        >
          <InteractiveEndorsedSkillRating
            score={currentRating}
            action={handleRating}
            compact
          />
        </Box>

        {isEndorsed && endorsementDate && (
          <Typography
            variant="caption"
            fontStyle="italic"
            color={palette.Text.SubtleText}
            sx={{ gridColumn: { xs: '1', sm: '1 / 6' } }}
          >
            Last updated by you on {moment(endorsementDate).format('DD MMM YYYY')}.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
